import { useState } from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import TrashIcon from "../../Icons/TrashIcon";
import DeleteModal from "../../Shared/DeleteModal";
import { IFRSQuestionDTO } from "../../../api/Quiz/GetIFRSQuestions";
import { DeleteIFRSQuestion } from "../../../api/Quiz/DeleteIFRSQuestion";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../../Shared/Button";

interface IFRSQuestionRowProps {
  question: IFRSQuestionDTO;
  examId: string;
  onDelete?: (questionId: string) => void;
}

const IFRSQuestionRow = ({
  question,
  examId,
  onDelete,
}: IFRSQuestionRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleDelete = async (id: string) => {
    const result = await DeleteIFRSQuestion(examId, id);
    if (!result.error) {
      if (onDelete) {
        onDelete(id);
      }
      queryClient.invalidateQueries({ queryKey: ["ifrs-questions", examId] });
      toast.success("Question deleted successfully");
    } else {
      toast.error("Failed to delete question");
    }
  };

  // Strip HTML tags for display (simple version)
  const stripHtml = (html: string | null) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncateText = (text: string | null, maxLength: number = 100) => {
    if (!text) return "N/A";
    const cleaned = stripHtml(text);
    return cleaned.length > maxLength
      ? cleaned.substring(0, maxLength) + "..."
      : cleaned;
  };

  return (
    <>
      <tr>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {question.id}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold max-w-md">
            {truncateText(question.description, 150)}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold max-w-md">
            {truncateText(question.requirements, 150)}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {question.marks !== null ? question.marks : "N/A"}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <Link to={`${question.id}/exhibits`}>
            <Button variant="gray" className="!h-8 !text-xs !px-4">
              Exhibits
            </Button>
          </Link>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <Link to={`${question.id}/response-options`}>
            <Button variant="gray" className="!h-8 !text-xs !px-4">
              Response Options
            </Button>
          </Link>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="flex justify-center items-stretch gap-3">
            <div className="flex flex-shrink-0 rounded-xl justify-center items-center border p-3 text-sm font-semibold cursor-pointer hover:bg-gray-50">
              <Link to={`edit/${question.id}`}>
                <Pencil size={18} />
              </Link>
            </div>
            <div className="flex flex-shrink-0 rounded-xl justify-center items-center border p-3 text-sm font-semibold cursor-pointer hover:bg-gray-50">
              <button onClick={() => setIsOpen(true)}>
                <TrashIcon />
              </button>
            </div>
          </div>
        </td>
      </tr>
      <DeleteModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        mutationFn={handleDelete}
        itemId={question.id}
        queryKey={`ifrs-questions-${examId}`}
      />
    </>
  );
};

export default IFRSQuestionRow;
